/** recordTimelineMerge — the pure lane→item projection + the CLIENT-SIDE merge behind
 * CrmRecordTimeline. Split out of the component (house pattern, cf. `recordReviewV2.ts`,
 * `crmFilterTree.ts`) so the ordering + gating rules are testable as data, not only through the DOM.
 *
 * Design of record: notes-provenance spec §8. The gate story and the lane endpoints live in
 * `CrmRecordTimeline.tsx`'s header — read that first. In short: three separately-gated reads, merged
 * here and only here; no server-side merge.
 *
 * E1b-2 removed `pendingItems` and everything it needed (`PendingItem`, the block-key subset, the
 * deadline sort, the v1-vs-v2 source gate). Not because that code was wrong — its source gate closed a
 * real disclosure hole in review — but because the surface it fed is gone: the timeline is now a
 * SECTION of the panel, and the same pending proposals render in the fields grid above it. The
 * reasoning is on `CrmRecordTimeline.tsx`; the code is in git if the queue ever comes back. Nothing
 * here reads the record payload any more: three lane reads, merged, and that is all.
 */
import { ApiError } from '../../api/client';
import type { CrmFieldHistoryEntry, CrmNoteListItem, EvidenceItem } from '../../types';
import { ensureUtc } from '../../utils/formatTimestamp';
import { wallClockInTzToUtcIso } from '../../utils/timezone';
import { groupBlockItems, type BlockGroup } from './crmBlockGroups';

/** The zone this surface prints AND filters in (timeline-row-layout-spec §3). One constant, because
 *  the printed date and the range boundary beside it must be the same zone or they contradict. */
const ET = 'America/New_York';

/** Every timestamp on this surface, parsed the one way.
 *
 * ⛔ The lanes carry NAIVE UTC (`datetime.isoformat()` on a naive column — no `Z`), which
 * `Date.parse` reads as browser-local. That is a silent per-reader shift of the whole stream: the
 * sort order, the range boundary and the printed date each absorbed it separately, and across a DST
 * change they absorbed DIFFERENT amounts. `ensureUtc` is the same normaliser `fmtWhen` and
 * `crmFormat` use, so the number a row is filtered by is the instant it prints. */
function parseInstant(iso: string | null | undefined): number {
  if (!iso) return NaN;
  return ensureUtc(iso).getTime();
}

/** The three reviewable narrative fields whose ACCEPTED values form the qualitative decision stream,
 * each mapped to the exact heading `proposal_lifecycle._accept_note_body` writes into the note it
 * materializes. One table, two jobs — the lane's row label AND the twin-pairing label. They must not
 * drift: if the backend renames a heading, this constant renames with it.
 *
 * ⚠ Do NOT read the label off `record_review.v2`'s `fields[].label` for a block: the server emits the
 * raw field key there (`"label": bkey` in `record_review._build_fields_and_members`), so it would
 * render "recent_updates.progress" to an operator. */
export const UPDATE_LABELS: readonly { fieldKey: string; label: string }[] = [
  { fieldKey: 'recent_updates.progress', label: 'Deal Progress' },
  { fieldKey: 'recent_updates.new_info', label: 'New Info' },
  { fieldKey: 'next_steps', label: 'Next Steps' },
];

export type TimelineKind = 'evidence' | 'updates' | 'notes';

/** One settled row in the merged stream. `sortKey` is the lane's own timestamp (spec §8); a row with
 * no timestamp sorts last rather than being dropped — a dateless row is still history. */
export interface TimelineItem {
  key: string;
  kind: TimelineKind;
  sortKey: string | null;
  title: string;
  meta: string;
  /** Grouped bullet runs (`groupBlockItems`). Empty for the evidence and notes lanes, which have no
   * block value. A flat pre-S2 block is exactly one run with `group: null`. */
  groups: BlockGroup[];
  body: string | null;
  noteId: number | null;
  /** UPDATES lane only: this row is an ACCEPTANCE (`ACCEPT_EVENT_KINDS`), not a plain value change.
   * Only an acceptance can have materialized a note, so only an acceptance may retire one. */
  acceptKind?: boolean;
  /** EVIDENCE lane only (Pillar F.5): the `deal_event_log` id this row is, and whether the server says
   * its body is readable by this caller. `body` is ALWAYS null on an evidence row — the text is not on
   * the wire at all; the stream renders an expander from `bodyAvailable` and fetches on click. */
  evidenceEventId?: number;
  bodyAvailable?: boolean;
  /** EVIDENCE lane only (E1b-0): the server's `may_refute_event` for this row and this caller. The ⚑
   * renders from this and nothing else. */
  canRefute?: boolean;
  /** EVIDENCE lane only: mailbox scans collapsed into this row (>1 renders the "+N inboxes" badge the
   * Evidence sub-tab has always had — dropped here until E1b-0, so one email seen by three colleagues
   * read as an ordinary single row on the stream). */
  inboxCount?: number;
}

/** A stream row's date, in ET from the `ensureUtc`-normalised instant (timeline-row-layout-spec
 *  §3) — the same pair of rules `crmFormat.fmtShortDate` applies, so the two timeline surfaces
 *  cannot print one email on two different days. */
export function fmtWhen(iso: string | null): string {
  if (!iso) return '—';
  const d = ensureUtc(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US',
                          { month: 'short', day: 'numeric', year: 'numeric', timeZone: ET });
}

function fmtTo(to: string[] | null | undefined): string {
  const list = to ?? [];
  if (list.length === 0) return '';
  if (list.length <= 2) return list.join(', ');
  return `${list.slice(0, 2).join(', ')} +${list.length - 2}`;
}

/** A version row's value is either a block (a list of items) or `next_steps`' long_text. Grouped runs
 * for the first, a paragraph for the second — the same split the note materializer makes.
 *
 * ⛔ Grouping comes from the SHARED `groupBlockItems`, never a local projection. This module carried a
 * third private copy of the nullish-coalesce-then-trim text projection until 2026-08-20, and it
 * was wrong three ways at
 * once: no group sub-headers (so flipping `crm_record_timeline_enabled` would have shown grouped
 * bullets in the Fields grid and flat ones in the timeline on the SAME screen); legacy bare-string
 * items projected to `''`, which made `updatesLaneItems` drop the whole version row from the stream
 * and `pendingItems` offer Accept / Dismiss on a proposal rendering as nothing; and a non-string
 * `text` THREW, unmounting the panel. */
function updateValue(value: unknown): { groups: BlockGroup[]; body: string | null } {
  if (Array.isArray(value)) return { groups: groupBlockItems(value), body: null };
  if (typeof value === 'string' && value.trim()) return { groups: [], body: value.trim() };
  return { groups: [], body: null };
}

/** Version-stream `event_kind` values that mean "a proposal was ACCEPTED". `committed_edit` is
 * deliberately NOT here: `lists._append_edit_version` writes it for a PLAIN CELL EDIT (HistoryPolicy's
 * default `event_kind`, `method='edit'`), which resolves no proposal and materializes no note. The
 * proposal path also maps `decided_via='edit'` (edit-and-accept) onto `committed_edit`, and the two are
 * indistinguishable on the wire — `field_history` returns version/value/event_kind/actor/method/
 * occurred_at and NOT `source_proposal_id`, the one column that separates them. So `committed_edit` is
 * treated as an edit throughout: see `updateMeta` for the label and `twinCandidates` for pairing. */
const ACCEPT_EVENT_KINDS: readonly string[] = ['row_accepted', 'auto_accepted'];

/** "who did this, and how" — the provenance line, which is the whole point of this surface.
 *
 * Two discriminators, each used for what it actually means (review finding 3):
 *   · `method === 'scheduled_job'` — machine vs human. Ruling #783 put that discriminator on `method`
 *     precisely so nobody string-parses `actor`; it stays there.
 *   · `event_kind` — an acceptance vs a plain value change. Labelling a `committed_edit` row "accepted
 *     by X" attributed a human's OWN cell edit to their acceptance of an AI proposal: a provenance
 *     error, not a wording nit. An edit-and-accept also lands here and reads "edited by X", which is
 *     true of it too — the operator's edited value is what published. */
function updateMeta(e: CrmFieldHistoryEntry): string {
  if (e.method === 'scheduled_job') return 'auto-accepted';
  if (e.event_kind === 'committed_edit') return e.actor ? `edited by ${e.actor}` : 'edited';
  return e.actor ? `accepted by ${e.actor}` : 'accepted';
}

export function evidenceLaneItems(rows: EvidenceItem[]): TimelineItem[] {
  return rows.map((ev) => {
    const to = fmtTo(ev.recipients_to);
    return {
      key: `evidence:${ev.event_log_id}`,
      kind: 'evidence' as const,
      sortKey: ev.occurred_at,
      title: ev.subject || '(no subject)',
      meta: [ev.sender || '—', to && `→ ${to}`].filter(Boolean).join(' '),
      groups: [],
      // F.5 — no body text rides the evidence lane's payload any more, so there is nothing to put
      // here. The stream renders an on-demand expander off `bodyAvailable` instead.
      body: null,
      noteId: null,
      evidenceEventId: ev.event_log_id,
      bodyAvailable: ev.body_available,
      canRefute: ev.can_refute,
      inboxCount: ev.inbox_count,
    };
  });
}

export function updatesLaneItems(
  streams: { label: string; entries: CrmFieldHistoryEntry[] }[],
): TimelineItem[] {
  const out: TimelineItem[] = [];
  for (const { label, entries } of streams) {
    for (const e of entries) {
      const { groups, body } = updateValue(e.value);
      if (groups.length === 0 && body === null) continue;   // an emptied value says nothing
      out.push({
        key: `updates:${label}:${e.version}`,
        kind: 'updates',
        sortKey: e.occurred_at,
        title: label,
        meta: updateMeta(e),
        groups,
        body,
        noteId: null,
        acceptKind: ACCEPT_EVENT_KINDS.includes(e.event_kind ?? ''),
      });
    }
  }
  return out;
}

/** Which materialized-update heading this note preview opens with, or null.
 *
 * WORD-BOUNDARY, not bare prefix (review finding 4): a plain `startsWith` also hid "New Information on
 * the perimeter…" and "Deal Progression…", both ordinary human notes. The label must be followed by
 * end-of-text or a non-alphanumeric character — which is what the materializer always produces (the
 * label is a heading node, so the next collected text starts a new block). Display-only either way. */
export function materializedUpdateLabel(preview: string | null): string | null {
  const text = (preview ?? '').trimStart();
  for (const { label } of UPDATE_LABELS) {
    if (!text.startsWith(label)) continue;
    const next = text.charAt(label.length);
    if (next === '' || !/[A-Za-z0-9]/.test(next)) return label;
  }
  return null;
}

/** How far apart the version row and its materialized note may be stamped and still be the same
 * decision. They are written in ONE transaction but from TWO `utcnow()` calls — the resolver's `now`
 * for the version row, `notes.create_note`'s own `now` for the note — so they are milliseconds, not
 * zero, apart. Five minutes is enormous next to that and still far below any real re-accept interval
 * for the same block (proposals arrive per aggregator run; the auto-accept sweep is weekly). */
const TWIN_PAIRING_TOLERANCE_MS = 5 * 60_000;

/** The accept-kind rows a note could be the twin of. Filtered by `event_kind` (see
 * `ACCEPT_EVENT_KINDS`) so a plain cell edit — which materializes NO note — can never be mistaken for
 * one and eat a real note that happens to share its label and moment (review finding 1b). */
function twinCandidates(updateItems: TimelineItem[]): { label: string; at: number }[] {
  const out: { label: string; at: number }[] = [];
  for (const u of updateItems) {
    if (!u.acceptKind || !u.sortKey) continue;
    const at = parseInstant(u.sortKey);
    if (!Number.isNaN(at)) out.push({ label: u.title, at });
  }
  return out;
}

/** The notes lane, with the materialized twin of an accepted update removed.
 *
 * ⛔ SUPPRESSION IS PAIRED 1:1 AND ACCEPT-ONLY, NEVER UNCONDITIONAL. Two review rounds hardened this,
 * both times because THE GUARANTEE below was false in a way that LOST history:
 *
 *   the guarantee — worst case a visible double-print (cosmetic), never a disappearance (data-trust).
 *
 * Round 1: suppression ignored the stream entirely. `_maybe_append_value_version` is dark behind
 * `crm_field_value_versioning_enabled` (**code default False**) while note materialization is not, so
 * versioning-off emptied the updates lane AND hid every twin — the accepted qualitative history
 * vanished from the surface built to display it.
 *
 * Round 2, the two defects that survived that fix:
 *   (a) MANY-TO-ONE. A `some()` probe let ONE version row suppress every note matching it, so N notes
 *       against 1 row lost N-1. Candidates are CONSUMED as they are matched (closest first), so k
 *       version rows can retire at most k notes.
 *   (b) WRONG ROW KINDS. Pairing accepted any row, including `committed_edit` from a plain cell edit,
 *       which materializes no note — so an ordinary edit could silently eat a real note sharing its
 *       label and moment. Only `ACCEPT_EVENT_KINDS` rows are candidates now.
 *
 * Deliberate cost of (b): edit-and-accept ALSO lands `committed_edit` and does materialize a note, and
 * nothing on the wire separates the two (no `source_proposal_id` in `field_history`). So an
 * edit-and-accepted block double-prints. That is the guarantee working as designed — the cosmetic
 * failure, chosen over the data-trust one. */
export function notesLaneItems(rows: CrmNoteListItem[], updateItems: TimelineItem[]): TimelineItem[] {
  const candidates = twinCandidates(updateItems);
  const kept: CrmNoteListItem[] = [];
  for (const n of rows) {
    const label = materializedUpdateLabel(n.preview_text);
    const noteAt = parseInstant(n.created_at);
    if (label !== null && !Number.isNaN(noteAt)) {
      // Closest match wins, so two accepts of the same block retire the right notes rather than the
      // first-listed ones. best === -1 means nothing is in range and the note is real history.
      let best = -1;
      let bestDelta = Infinity;
      for (let i = 0; i < candidates.length; i += 1) {
        if (candidates[i].label !== label) continue;
        const delta = Math.abs(candidates[i].at - noteAt);
        if (delta <= TWIN_PAIRING_TOLERANCE_MS && delta < bestDelta) {
          best = i;
          bestDelta = delta;
        }
      }
      if (best >= 0) {
        candidates.splice(best, 1);   // CONSUMED — it cannot retire a second note
        continue;
      }
    }
    kept.push(n);
  }
  return kept.map((n) => ({
    key: `notes:${n.id}`,
    kind: 'notes' as const,
    sortKey: n.created_at,
    title: n.preview_text || '(empty)',
    // `author === null` can ONLY be a system note (create_note always stamps one) — the same reading
    // CrmNotesSection uses; 'Someone' would read like a bug.
    meta: n.author?.name ?? (n.author === null ? 'AI' : 'Someone'),
    groups: [],
    body: null,
    noteId: n.id,
  }));
}

/** Newest first, dateless rows last, `key` as the stable tie-break so equal timestamps never reorder
 * between renders. THE client-side merge of spec §8 — there is no server-side equivalent. */
export function mergeTimeline(lanes: TimelineItem[][]): TimelineItem[] {
  return lanes.flat().sort((a, b) => {
    const at = parseInstant(a.sortKey);
    const bt = parseInstant(b.sortKey);
    const aBad = Number.isNaN(at);
    const bBad = Number.isNaN(bt);
    if (aBad !== bBad) return aBad ? 1 : -1;      // dateless sorts last, never dropped
    if (!aBad && at !== bt) return bt - at;       // newest first
    return a.key < b.key ? -1 : a.key > b.key ? 1 : 0;
  });
}

/** A lane refusal is a LANE problem: name it in place and keep merging the others (the whole point of
 * three separate governed doors). STRICT — any error at all is a refusal. Used by the evidence and
 * notes lanes. */
export function laneError(error: unknown): boolean {
  return error != null;
}

/** The UPDATES lane only. `lists.field_history` raises `BadGovernanceRequest` → 400 for "no such field
 * on this list", i.e. this list simply has no Progress / New-Info / next_steps column — an empty
 * stream, not a governance failure, and it must not put a refusal notice on the panel.
 *
 * ⚠ Scoped to this ONE lane on purpose (review finding 7). No genuine authorization failure surfaces
 * as 400 on these routes TODAY (a denial is an existence-safe 404, `NotEntitled` is 403), but
 * `lists.py` already raises `BadGovernanceRequest` for a visibility refusal elsewhere in the same
 * module, so "400 is always benign" is a coincidence of today's code, not a guarantee. Swallowing it
 * lane-wide would silently hide a future refusal on the evidence or notes door. */
export function updatesLaneError(error: unknown): boolean {
  if (error == null) return false;
  if (error instanceof ApiError && error.status === 400) return false;
  return true;
}

/* ── the date-range filter (IOT panel revision, 2026-08-22) ─────────────────────────────────────
 *
 * A second filter axis beside the kind chips: how far back the stream reaches. Client-side, over the
 * already-merged rows — the same "no server-side merge endpoint" constraint that shapes everything
 * else on this surface, so a range cannot be pushed into three separately-gated lane reads without
 * inventing one.
 *
 * FOUR RELATIVE PRESETS AND AN EXPLICIT FROM/TO (owner ruling 2026-08-22). The presets answer "what
 * happened recently" in one click; the pickers answer "what happened around the LOI in March", which
 * a preset cannot express at all. Same shape as the LLM-spend chart's window control
 * (`LlmSpendChart`, `window === 'custom'` reveals two native date inputs) so the firm's two
 * date-window controls do not read as two different ideas.
 */

export type TimelinePreset = '30d' | '90d' | '12mo' | 'all' | 'custom';

export const RANGE_PRESETS: readonly { key: TimelinePreset; label: string; days: number | null }[] = [
  { key: '30d', label: '30 days', days: 30 },
  { key: '90d', label: '90 days', days: 90 },
  { key: '12mo', label: '12 months', days: 365 },
  { key: 'all', label: 'All time', days: null },
  { key: 'custom', label: 'Custom…', days: null },
];

/** The whole axis in one value. `from` / `to` are the native pickers' 'YYYY-MM-DD' (empty = open-ended)
 * and are read ONLY under `preset === 'custom'` — a preset switch therefore does not have to clear
 * them, so flipping to 30 days and back to Custom keeps the dates the operator typed. */
export interface TimelineRange {
  preset: TimelinePreset;
  from: string;
  to: string;
}

export const RANGE_ALL: TimelineRange = { preset: 'all', from: '', to: '' };

/** What the panel OPENS on — owner ruling 2026-08-22: the last 30 days, not all time.
 *
 * ⛔ THIS MAKES THE `olderCount` FOOTER LOAD-BEARING, not cosmetic. A default range means the panel
 * arrives already filtered — so on a deal whose last activity was in March, the operator's first
 * sight of the timeline is an EMPTY stream they did not filter. The footer stating "+161 older than
 * this range" is then the only thing standing between them and "the timeline is broken / this deal
 * has no history", which is why it has to be an ACTION (switch to All time) rather than a note, and
 * why the empty state has to name the range instead of saying "nothing recorded on this deal yet".
 * Both are pinned by tests. If a future change makes that footer conditional, quiet, or unclickable,
 * this default has to go back to `RANGE_ALL` in the same change. */
export const RANGE_DEFAULT: TimelineRange = { preset: '30d', from: '', to: '' };

/** A picker value → ET midnight, or null if it is absent or not a real date.
 *
 * ⛔ THE BOUNDARY ZONE IS THE ZONE THE ROWS PRINT IN, and that is the whole rule — not a preference,
 * and no longer the BROWSER's zone. `fmtWhen` renders every row in ET (timeline-row-layout-spec §3),
 * so an email stamped 2026-03-02T01:00Z READS as March 1 on screen; a boundary built in UTC (or in a
 * Pacific reader's local zone) would put that row outside a from=2026-03-01 range while the row beside
 * the control says March 1 — a filter contradicting the dates next to it, on the surface whose whole
 * job is when-things-happened. This printed in browser-local once and the boundary was browser-local
 * for exactly this reason; the two moved to ET together, and they must never move apart.
 *
 * The round-trip check rejects a date the constructor would silently roll over (2026-02-31 → Mar 3),
 * which `<input type="date">` will not produce but a deep link or a paste can. It runs in UTC so the
 * probe itself cannot be perturbed by a zone.
 *
 * `wallClockInTzToUtcIso` is the shared DST-correct converter (`utils/timezone.ts`); it returns a
 * naive UTC ISO, hence the appended `Z`. */
function etDayStart(value: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const probe = new Date(Date.UTC(y, mo - 1, d));
  if (probe.getUTCFullYear() !== y || probe.getUTCMonth() !== mo - 1 || probe.getUTCDate() !== d) {
    return null;
  }
  const ms = Date.parse(wallClockInTzToUtcIso(`${m[1]}-${m[2]}-${m[3]}T00:00`, ET) + 'Z');
  return Number.isNaN(ms) ? null : ms;
}

/** The END of a picker day, exclusive — the NEXT ET midnight, so `to` is INCLUSIVE of everything
 * that happened on that date. A half-open upper bound built by hand (`+ 86_400_000`) would be wrong
 * across a DST change; asking for the next calendar day's ET midnight is not — March 8 is 23 hours
 * long and November 1 is 25, and both are pinned by tests. The calendar increment runs in UTC, where
 * no day is ever missing an hour. */
function etDayEndExclusive(value: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!m || etDayStart(value) === null) return null;
  const next = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + 1));
  return etDayStart(next.toISOString().slice(0, 10));
}

export interface RangeSplit {
  shown: TimelineItem[];
  /** Dated rows BEFORE the window, and dated rows AFTER it (only a `to` bound can produce the second).
   * Two counts, not one: "+161 older" under a March window would be a lie about the 40 rows from
   * April onward. */
  olderCount: number;
  newerCount: number;
  undated: TimelineItem[];
  /** A custom range that cannot be honoured: an unparseable picker value, or from after to. The
   * caller says so and shows NOTHING — a control claiming a range while the stream ignores it is the
   * one-record-contradicting-itself class. */
  invalid: boolean;
}

/** Split the stream at the range boundary rather than filtering it, because FOUR counts matter and a
 * filter only produces one: what to show, how many dated rows fell either side, and how many rows have
 * no date at all.
 *
 * ⛔ A DATELESS ROW IS NEVER SILENTLY DROPPED. `mergeTimeline` keeps one (sorted last) on the
 * principle that a dateless row is still history — but it cannot honestly be shown as "the last 30
 * days" either, so an active range puts it in `undated` and the caller says so in a footer. Both
 * failure modes this avoids were real on other surfaces: history that vanishes with no trace, and a
 * count that claims to be complete while a filter quietly trims it.
 *
 * `now` is injected so the boundary is testable without freezing the clock.
 */
export function splitByRange(
  items: TimelineItem[], range: TimelineRange, now: number = Date.now(),
): RangeSplit {
  const none: RangeSplit = {
    shown: items, olderCount: 0, newerCount: 0, undated: [], invalid: false,
  };
  let lower = -Infinity;
  let upper = Infinity;

  if (range.preset === 'custom') {
    if (range.from.trim() !== '') {
      const v = etDayStart(range.from);
      if (v === null) return { ...none, shown: [], invalid: true };
      lower = v;
    }
    if (range.to.trim() !== '') {
      const v = etDayEndExclusive(range.to);
      if (v === null) return { ...none, shown: [], invalid: true };
      upper = v;
    }
    if (lower === -Infinity && upper === Infinity) return none;   // Custom with neither date = All
    if (lower >= upper) return { ...none, shown: [], invalid: true };
  } else {
    const days = RANGE_PRESETS.find((r) => r.key === range.preset)?.days ?? null;
    if (days === null) return none;                                // 'all'
    lower = now - days * 24 * 60 * 60 * 1000;
  }

  const shown: TimelineItem[] = [];
  const undated: TimelineItem[] = [];
  let olderCount = 0;
  let newerCount = 0;
  for (const it of items) {
    const at = parseInstant(it.sortKey);
    if (Number.isNaN(at)) undated.push(it);
    else if (at < lower) olderCount += 1;
    else if (at >= upper) newerCount += 1;
    else shown.push(it);
  }
  return { shown, olderCount, newerCount, undated, invalid: false };
}
